import React, { useEffect, useRef, useState } from 'react';
import { Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ComposedChart, Line, Legend, Cell } from 'recharts';
import Skeleton from '@mui/material/Skeleton';

const UtilityTrendGraph = props => {
    const formatYAxisTick = tick => {
        let prefix = props.activeParameter === 'cost' ? '$' : '';
        
        if (Math.abs(tick) >= 1e6) return prefix + (tick / 1e6).toFixed(1) + 'M'; 
        else if (Math.abs(tick) >= 1e3) return prefix + (tick / 1e3).toFixed(0) + 'k';
        else return prefix + tick.toLocaleString();
    }

    const formatXAxisTick = tick => {
          if(tick == null) return '';

        if (typeof tick === 'number') return '';

        const s = String(tick);
        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    const formatReadings = (value, name, item) => {
        const row = item?.payload;
        const date = row?.date;

        const idx = combinedArr.findIndex(r => r.date === date);

        let units = '';
        if (['water', 'gas'].includes(props.activeParameter)) units = 'm³';
        else if (props.activeParameter === 'electricity') units = 'kWh';

        if (name === 'HDD (18°C)' || name === 'CDD (18°C)' || name === 'Baseline') {
            return [`${Math.round(value).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`, name];
        }

        if (props.activeParameter === 'ghg') {
            return [
                `${Math.round(value / 1000) === 0 ? '< 1' : Math.round(value / 1000).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} MT CO₂e`,
                props.barType === 'total' ? name.substring(0,1).toUpperCase() + name.substring(1) : name,,
            ];
        }

        if (props.activeParameter === 'cost') {
            return [
                `$${Math.round(value).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`,
                props.barType === 'total' ? name.substring(0,1).toUpperCase() + name.substring(1) : name,
            ];
        }

        const ap = (props.activeParameter || '').toLowerCase();

        const src = getSourceForTooltipItem({
            name,
            ap,
            props,
            combinedArr,
            barType: props.barType,
            index: idx,
        });

        const suffix = sourceSuffix(src);

        return [
            `${Math.round(value).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} ${units} ${suffix}`.trim(),
            props.barType === 'total' ? props.activeParameter.substring(0,1).toUpperCase() + props.activeParameter.substring(1) : name,
        ];
    }

    const sourceSuffix = (src) => {
        if (src === 'bills') return '(B)';
        if (src === 'sensors') return '(S)';
        if (src === 'estimate') return '(E)';
        return '';
    }

    const getSourceForTooltipItem = ({ name, ap, props, combinedArr, barType, index }) => {
        if (index < 0) return '';

        if (barType === 'total') {
            return combinedArr?.[index]?.[ap]?.source || '';
        }

        return props?.siteResults?.[name]?.result?.[index]?.[ap]?.source || '';
    }

    const getLegendFlags = (data) => {
        const arr = Array.isArray(data) ? data : [];
        const utilities = ['water', 'electricity', 'gas'];

        const showBaseline = ['electricity', 'gas'].includes(props.activeParameter);
        const showCDD = props.activeParameter === 'electricity';
        const showHDD = props.activeParameter === 'gas';

        const hasBills = arr.some(d => utilities.some(u => d?.[u]?.source === 'bills'));
        const hasEstimate = arr.some(d => utilities.some(u => d?.[u]?.source === 'estimate'));
        const hasSensors = arr.some(d => utilities.some(u => d?.[u]?.source === 'sensors'));

        return { hasBills, hasEstimate, hasSensors, showBaseline, showHDD, showCDD };
    };

    const {
        hasBills, hasSensors, hasEstimate,
        showBaseline, showCDD, showHDD
    } = getLegendFlags(props.combinedData);

    const CustomLegend = ({ 
        activeParameter, hasBills, hasSensors, hasEstimate,
        showBaseline, showCDD, showHDD,
        color = '#1f77b4', barType
     }) => {
        const Swatch = ({ style }) => (
            <span
            style={{
                width: 12,
                height: 12,
                display: 'inline-block',
                borderRadius: 2,
                ...style,
            }}
            />
        );

        const Item = ({ label, swatch }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Swatch style={swatch} />
                <span>{label}</span>
            </div>
        );

        const isGhg = activeParameter === 'ghg';
        const isCost = activeParameter === 'cost';

        if (isGhg || isCost) {
            return (
                barType === 'total' ?
                <div style={{ display: 'flex', flexDirection: props.isMobile ? 'row' : 'column', flexWrap: 'wrap', gap: 10 }}>
                        <Item
                            label={isGhg ? 'Water emissions' : 'Water cost'}
                            swatch={{ background: 'rgb(8,114,182)' }}
                        />
                        <Item
                            label={isGhg ? 'Electricity emissions' : 'Electricity cost'}
                            swatch={{ background: 'rgb(213,43,0)' }}
                        />
                        <Item
                            label={isGhg ? 'Gas emissions' : 'Gas cost'}
                            swatch={{ background: 'rgb(0,177,168)' }}
                        />
                </div>
                :
                <div>
                    <Item
                        label={isGhg ? 'Site emissions' : 'Site costs'}
                        swatch={{ background: 'rgb(8,114,182)' }}
                    />
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', flexDirection: props.isMobile ? 'row' : 'column', flexWrap: 'wrap', gap: 10 }}>
                {hasBills && <Item label="Bills (B)" swatch={{ background: color }} />}

                {hasSensors && (
                    <Item
                        label="Sensors (S)"
                        swatch={{
                        background: `repeating-linear-gradient(45deg, ${color} 0 3px, transparent 3px 6px)`,
                        border: `1px solid ${color}`,
                        }}
                    />
                )}

                {hasEstimate && (
                    <Item
                        label="Estimate (E)"
                        swatch={{
                        background: 'transparent',
                        border: `2px solid ${color}`,
                        }}
                    />
                )}

                {showBaseline && (
                    <Item label={`Baseline (${Math.round(100 * props.baselinePcts[props.activeParameter])}%)`} swatch={{ background: 'rgb(0,177,168)' }} />
                )}

                {showCDD && (
                    <Item label="CDD (18°C)" swatch={{ background: 'rgb(213,43,0)' }} />
                )}

                {showHDD && (
                    <Item label="HDD (18°C)" swatch={{ background: 'rgb(213,43,0)' }} />
                )}
            </div>
        );
    }

    const siteNames = props.selectedSiteNames || [];
    const combinedArr = Array.isArray(props.combinedData) ? props.combinedData : [];

    const buildSiteUtilityStack = (util) => {
        if (!siteNames.length) return [];
        const base = props.siteResults[siteNames[0]]?.result || [];

        return base.map((m, idx) => {
            const row = {
                date: m.date,
                cdd_18c: m.cdd_18c ?? 0,
                hdd_18c: m.hdd_18c ?? 0,
            };

            row[util] = {
                baseline: combinedArr?.[idx]?.[util]?.baseline ?? 0,
            };

            for (const s of siteNames) {
                row[s] = props.siteResults[s]?.result?.[idx]?.[util]?.total ?? 0;
            }

            return row;
        });
    };

    const buildSiteTotalStack = (metric) => {
        if (!siteNames.length) return [];
        const base = props.siteResults[siteNames[0]]?.result || [];
        return base.map((m, idx) => {
            const row = { date: m.date };
            for (const s of siteNames) {
            const r = props.siteResults[s]?.result?.[idx];
            if (!r) { row[s] = 0; continue; }

            row[s] =
                metric === 'cost'
                ? (r.totalCost ?? 0)
                : ((r.ghgMT ?? 0) * 1000);
            }
            return row;
        });
    };

    const ap = (props.activeParameter || '').toLowerCase();

    const barDataKey =
        ap === 'ghg'  ? 'ghgMT' :
        ap === 'cost' ? 'totalCost' :
        `${ap}.total`;

    const isGhgOrCost = ap === 'ghg' || ap === 'cost';

    const chartData = 
    isGhgOrCost
        ? (props.barType === 'total' ? (ap === 'ghg' ? props.emissionTrends : props.costTrends) : buildSiteTotalStack(ap))
        : (props.barType === 'total' ? combinedArr : buildSiteUtilityStack(ap));

    if(props.isLoading) {
        return (
            <div style={{ width: '100%', height: '100%' }}>
                <Skeleton variant="rectangular" width="100%" height="100%" animation="wave"/>
            </div>
        );
    }

    return (
        <ResponsiveContainer height={props.isMobile ? 500 : '100%'} width='100%'>
            <ComposedChart data={chartData}>
                <defs>
                    <pattern id="billsFill" patternUnits="userSpaceOnUse" width={4} height={4}>
                        <rect width="4" height="4" fill="rgb(8,114,182)" />
                    </pattern>

                    <pattern id="sensorStripes" patternUnits="userSpaceOnUse" width={6} height={6}>
                        <rect width="6" height="6" fill="rgb(8,114,182)" />
                        <path d="M-1,1 l2,-2
                                M0,6 l6,-6
                                M5,7 l2,-2"
                            stroke="#ffffff"
                            strokeWidth="1"
                        />
                    </pattern>
                </defs>

                <XAxis dataKey="date" tickLine={props.isMobile} axisLine={false} tick={{ fontSize: props.isMobile ? '10px' : '12px', fill: 'rgb(78,79,83)'}} 
                    tickFormatter={formatXAxisTick} textAnchor='middle'/>

                <YAxis yAxisId="left" tickLine={false} axisLine={false} tickFormatter={formatYAxisTick} tick={{ fontSize: props.isMobile ? '10px' : '12px', fill: 'rgb(78,79,83)'}}
                    label={{
                        value:
                            props.activeParameter === 'cost' ? 'Utility expenditures' : props.activeParameter === 'ghg' ? 'GHG emissions (tonnes CO2)' :
                            `${props.activeParameter?.substring(0,1).toLocaleUpperCase()}${props.activeParameter?.substring(1, props.activeParameter.length)} Consumption (${props.activeParameter === 'electricity' ? 'kWh' : 'm3'})`
                        ,
                    position: 'outside', offset: 0, angle: -90, dx: -24, style: { fontSize: props.isMobile ? '10px' : '12px', fill: 'rgb(78,79,83)'}}}/>
                
                <YAxis yAxisId="right" orientation='right' tickLine={false} axisLine={false} tickFormatter={formatYAxisTick} tick={{ fontSize: props.isMobile ? '11px' : '14px', fill: 'rgb(78,79,83)'}}
                    label={{value: `${!['electricity', 'gas'].includes(props.activeParameter) ? '' : `${props.activeParameter === 'electricity' ? 'Cooling' : 'Heating'} degree days (${props.activeParameter === 'electricity' ? 'above' : 'below'} 18C)`}`,
                    position: 'outside', offset: 0, angle: 90, dx: 24, style: { fontSize: props.isMobile ? '10px' : '12px', fill: 'rgb(78,79,83)' }}}
                />
                    
                <Tooltip 
                    contentStyle={{
                        backgroundColor: '#F5F5F5',
                        color: '#333',
                        border:'1px solid lightgrey',
                        fontSize: '0.75em',
                    }}
                    labelFormatter={formatXAxisTick}
                    wrapperStyle={{ outline: "none" }}
                    formatter={formatReadings}
                />

                <Legend
                    layout={props.isMobile ? 'horizontal' : 'vertical'}
                    verticalAlign={props.isMobile ? 'bottom' : 'middle'}
                    align={props.isMobile ? 'bottom' : 'right'}
                    wrapperStyle={{ paddingLeft: 12 }}
                    content={() => (
                        <CustomLegend
                            activeParameter={ap}
                            hasBills={hasBills}
                            hasSensors={hasSensors}
                            hasEstimate={hasEstimate}
                            showBaseline={showBaseline}
                            showCDD={showCDD}
                            showHDD={showHDD}
                            color="#1f77b4"
                            barType={props.barType}
                        />
                    )}
                />
        
                <CartesianGrid stroke="#f5f5f5"/>

                {
                    isGhgOrCost ? (
                    props.barType === 'total' ? (
                        <>
                        <Bar dataKey="water" yAxisId="left" stackId="a" fill="rgb(8,114,182)" />
                        <Bar dataKey="electricity" yAxisId="left" stackId="a" fill="rgb(213,43,0)" />
                        <Bar dataKey="gas" yAxisId="left" stackId="a" fill="rgb(0,177,168)" />
                        </>
                    ) : (
                        <>
                        {siteNames.map((s) => (
                            <Bar key={s} dataKey={s} yAxisId="left" stackId="a" fill="rgb(8,114,182)" />
                        ))}
                        </>
                    )
                    ) : (
                    props.barType === 'total' ? (
                        <Bar yAxisId="left" dataKey={`${ap}.total`}>
                            {combinedArr.map((entry, index) => {
                                const src = entry?.[ap]?.source;
                                let cellProps = { fill: 'rgb(8,114,182)' };
                                if (src === 'sensors') cellProps.fill = 'url(#sensorStripes)';
                                if (src === 'estimate') {
                                cellProps = { fill: 'rgba(8,114,182,0.25)', stroke: 'rgb(8,114,182)', strokeWidth: 2, strokeDasharray: '4 2' };
                                }
                                return <Cell key={`cell-${index}`} {...cellProps} />;
                            })}
                        </Bar>
                    ) : (
                        <>
                        {siteNames.map((s) => (
                            <Bar
                                key={s}
                                dataKey={s}
                                yAxisId="left"
                                stackId="a"
                                fill="rgb(8,114,182)"
                                name={s}
                            >
                                {chartData.map((_, idx) => {
                                const src = props.siteResults?.[s]?.result?.[idx]?.[ap]?.source;

                                let cellProps = { fill: 'rgb(8,114,182)' };

                                if (src === 'sensors') {
                                    cellProps = { fill: 'url(#sensorStripes)' };
                                }

                                if (src === 'estimate') {
                                        cellProps = {
                                        fill: 'rgba(8,114,182, 0.25)',
                                        stroke: 'rgb(8,114,182)',
                                        strokeWidth: 1,
                                        strokeDasharray: '1 2',
                                    };
                                }

                                return <Cell key={`${s}-cell-${idx}`} {...cellProps} />;
                                })}
                            </Bar>
                        ))}
                        </>
                    ))
                }

                {['electricity', 'gas'].includes(props.activeParameter) && (
                    <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey={`${props.activeParameter}.baseline`}
                        strokeWidth={3}
                        stroke="rgb(0,177,168)"
                        dot={false}
                        name="Baseline"
                    />
                )}

                {props.activeParameter === 'electricity' && (
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="cdd_18c"
                        strokeWidth={3}
                        stroke="rgb(213,43,0)"
                        dot={false}
                        name="CDD (18°C)"
                    />
                )}

                {props.activeParameter === 'gas' && (
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="hdd_18c"
                        strokeWidth={3}
                        stroke="rgb(213,43,0)"
                        dot={false}
                        name="HDD (18°C)"
                    />
                )}
            </ComposedChart>
        </ResponsiveContainer>
    );
}

export default UtilityTrendGraph;